// Single source of truth for HR status colours.
//
// Before this, ten pages each kept a private { bg, fg } map of hardcoded
// hex — identical copies in most cases, and quietly divergent in others
// (HRAdmin's KPI row coloured "late" with the danger tint while its own
// request table, and every other page, used warning). They were all
// light-theme values, so in dark mode they rendered as light stickers on a
// near-black page; PromptsPage had the same bug mirrored, hardcoded dark so
// it inverted in light mode.
//
// Every entry here resolves to the paired --tone-*-bg / --tone-*-fg custom
// properties, which flip with the theme and are contrast-measured at their
// definition in index.css. Shape is deliberately { bg, fg, border } so a page
// can spread it straight into the map it already had and keep its own layout
// and icons.

const RAW = {
  neutral: ["var(--bg-subtle)",       "var(--text-secondary)",  "var(--border-subtle)"],
  success: ["var(--tone-success-bg)", "var(--tone-success-fg)", "var(--tone-success-br)"],
  warning: ["var(--tone-warning-bg)", "var(--tone-warning-fg)", "var(--tone-warning-br)"],
  danger:  ["var(--tone-danger-bg)",  "var(--tone-danger-fg)",  "var(--tone-danger-br)"],
  info:    ["var(--tone-info-bg)",    "var(--tone-info-fg)",    "var(--tone-info-br)"],
  violet:  ["var(--tone-violet-bg)",  "var(--tone-violet-fg)",  "var(--tone-violet-br)"],
  orange:  ["var(--tone-orange-bg)",  "var(--tone-orange-fg)",  "var(--tone-orange-br)"],
  teal:    ["var(--tone-teal-bg)",    "var(--tone-teal-fg)",    "var(--tone-teal-br)"],
};

// `co` is an alias of `fg`. The pages being converted are split between the two
// names; carrying both means each one is a pure import swap with no edit to the
// JSX that reads it.
const V = Object.fromEntries(
  Object.entries(RAW).map(([k, [bg, fg, border]]) => [k, { bg, fg, co: fg, border }])
);

export const TONE_VARS = V;

// Which tone each status reads as. Nine attendance states need nine
// distinguishable hues, which is why Phase 3 added orange and teal: without
// them half-day collides with late, and holiday with WFH. Colour is only ever
// reinforcement here — every pill also carries a label, and most an icon.
export const ATTENDANCE_TONE = {
  present:        "success",
  late:           "warning",
  "half-day":     "orange",
  absent:         "danger",
  wfh:            "violet",
  leave:          "info",
  holiday:        "teal",
  not_checked_in: "neutral",
  weekend:        "neutral",
};

// Approval lifecycle, shared by leaves / WFH / regularisation.
export const REQUEST_TONE = {
  pending:   "warning",
  approved:  "success",
  rejected:  "danger",
  cancelled: "neutral",
};

export const DIFFICULTY_TONE = {
  beginner:     "success",
  intermediate: "warning",
  advanced:     "danger",
};

export function tone(name) {
  return V[name] || V.neutral;
}

function mapOf(table) {
  return Object.fromEntries(Object.entries(table).map(([k, t]) => [k, V[t]]));
}

// Pre-resolved { bg, fg, border } per status, for spreading into a page's
// existing map. `fallback` is what an unknown status gets.
export const ATTENDANCE_TONES = mapOf(ATTENDANCE_TONE);
export const REQUEST_TONES    = mapOf(REQUEST_TONE);
export const DIFFICULTY_TONES = mapOf(DIFFICULTY_TONE);
export const fallbackTone     = V.neutral;
